import React, { Component } from 'react';
import { connect } from 'react-redux';
import { withTranslation } from 'react-i18next';
import { withRouter } from 'react-router-dom';
import { boundMethod } from 'autobind-decorator';
import useLocalStorageState from 'use-local-storage-state';

import KnowledgeGraphFlow from './KnowledgeGraphFlow';
import SearchArea from './search/SearchArea';
import SearchResultsPanel from './search/SearchResultsPanel';
import styles from './graph.scss';

import { searchGraph } from 'store/modules/graph/graphSearchResults';
import { trackEvent } from 'utils/eventTracking';
import withEmbedded from 'root/hocs/withEmbedded';
import { changeUrlGivenCurrentQueryParams } from 'components/LinkWithQuery';
import KnowledgeGraphVis from './KnowledgeGraphVis';
import KnowledgeGraphHeaderOptions from './components/KnowledgeGraphHeaderOptions';
import { fetchGraphLayout, setEntitiesSidebar } from 'store/modules/graph/graph';
import KnowledgeGraphLayoutManager, {
  calculateLayoutArea
} from 'components/knowledgeGraph/KnowledgeGraphLayoutManager';
import { ErrorBoundary } from '../../error-handler/ErrorBoundary';

// --- Constants ---

// Source defining how the current graph view was determined
const VIEW_DETERMINATION_SOURCE = {
  FORCE: 'force', // Forced via prop
  ONLY_ONE: 'only_one', // Only 1 layout available, other one failed to generate
  PREFERENCE: 'preference', // User preference from local storage
  LAYOUT_SIZE: 'layout_size', // Automatic based on calculated layout area
  DEFAULT: 'default' // Fallback default
};

// Available graph view types
export const GRAPH_VIEWS = {
  FLOW: 'flow', // React Flow based view
  VIS: 'vis' // Vis.js based view
};

export const getLocalStorageKgViewPreferenceKey = knowledgeGraphId =>
  `veezoo.knowledgeGraph.${knowledgeGraphId}.viewPreference`;

// Threshold for Vis.js layout area. Above this, we might switch to Flow if it's smaller.
const VIS_AREA_READABILITY_THRESHOLD = 1200000;

// Debounce time for search input
const SEARCH_DEBOUNCE_MS = 500;

// Special index value for keyboard navigation when "Show More" is active
const SHOW_MORE_ACTIVE_INDEX = -1;

// Opacity for dimmed "Show More" button during keyboard navigation
const SHOW_MORE_DIMMED_OPACITY = 0.3;
const SHOW_MORE_NORMAL_OPACITY = 1;

// --- HOC for Local Storage Preference ---

const withLocalStoragePreference = WrappedComponent => props => {
  const { knowledgeGraphId } = props;
  const storageKey = getLocalStorageKgViewPreferenceKey(knowledgeGraphId);

  // Default to the new graph view (React Flow)
  const [viewPreference, setViewPreference] = useLocalStorageState(storageKey, {
    // we don't want to set a default value for the preference, only if it's explicitly set by the user by toggling the view
    storageSync: false
  });

  return <WrappedComponent {...props} viewPreference={viewPreference} setViewPreference={setViewPreference} />;
};

// --- Helper Function to Determine Graph View ---

/**
 * Determines which graph view (Flow or Vis) should be used based on a priority order:
 * 1. Forced prop (`forceInitialGraphView`)
 * 2. Only 1 layout available (other one failed to generate, if layout data is available)
 * 3. User preference (`viewPreference`)
 * 4. Layout size comparison (if layout data is available)
 * 5. Default (Flow)
 */
const determineGraphView = props => {
  const { forceInitialGraphView, viewPreference, graph } = props;

  // 1. Forced View (Highest Priority)
  if (forceInitialGraphView) {
    return {
      useFlowGraph: forceInitialGraphView === GRAPH_VIEWS.FLOW,
      source: VIEW_DETERMINATION_SOURCE.FORCE,
      isDefinitive: true // Forced view is always final
    };
  }

  // 2. Flow graph unavailable, note if vis is unavailable we can still show in physics mode
  const layoutsReady = graph && !graph.needsToFetchGraphLayout;
  if (layoutsReady && graph.visualNodeLayoutFlow === undefined) {
    return {
      useFlowGraph: false,
      source: VIEW_DETERMINATION_SOURCE.ONLY_ONE,
      isDefinitive: true // Only one layout available is definitive
    };
  }

  // 3. User Preference
  // Check specifically for boolean true/false, ignoring null/undefined
  if (viewPreference?.useFlowGraph === true || viewPreference?.useFlowGraph === false) {
    return {
      useFlowGraph: viewPreference.useFlowGraph,
      source: VIEW_DETERMINATION_SOURCE.PREFERENCE,
      isDefinitive: true // User preference is considered final unless overridden by force prop
    };
  }

  // 4. Layout Size Comparison (requires layout data)
  const layoutsSuccessful =
    layoutsReady && graph.visualNodeLayoutFlow?.length > 0 && graph.visualNodeLayoutVis?.length > 0;

  if (layoutsSuccessful) {
    const flowArea = calculateLayoutArea(graph.visualNodeLayoutFlow);
    const visArea = calculateLayoutArea(graph.visualNodeLayoutVis);

    // Logic: Prefer Vis if its area is reasonably small OR if it's larger but still smaller than Flow's area.
    const shouldUseVis =
      visArea <= VIS_AREA_READABILITY_THRESHOLD || (visArea > VIS_AREA_READABILITY_THRESHOLD && visArea <= flowArea);
    return {
      useFlowGraph: !shouldUseVis,
      source: VIEW_DETERMINATION_SOURCE.LAYOUT_SIZE,
      isDefinitive: true // Layout comparison is definitive once data is available
    };
  }

  // 5. Provisional Default (Lowest Priority)
  // Used when no definitive choice can be made yet (e.g., waiting for layout data)
  return {
    useFlowGraph: true, // Default to Flow graph
    source: VIEW_DETERMINATION_SOURCE.DEFAULT,
    isDefinitive: false // This choice is temporary until a definitive source is available
  };
};

// --- KnowledgeGraphContainer Component ---

class KnowledgeGraphContainer extends Component {
  constructor(props) {
    super(props);

    // Determine the *initial* state based on immediately available info
    const initialViewDecision = determineGraphView(props);

    this.state = {
      // --- View State ---
      useFlowGraph: initialViewDecision.useFlowGraph,
      isViewDefinitivelySet: initialViewDecision.isDefinitive, // Tracks if the view is final

      // --- Search State ---
      isFocusedOnSearch: false, // Is the search input / panel active?
      shouldFocusOnInput: false, // Trigger focus on the search input programmatically
      graphSearchScrollCount: 0, // Tracks pagination/offset for search results
      activeSelectableElement: 0, // Index for keyboard navigation in search results (0 = input, -1 = "Show More")

      // --- Graph Interaction State ---
      nodeUriToFocus: null // URI of the node to programmatically center/zoom on
    };

    this.searchGraphTimeout = null; // For debouncing search input
  }

  // --- Lifecycle Methods ---
  componentDidUpdate(prevProps, prevState) {
    // Recalculate the desired view based on potentially updated props (e.g., graph layout loaded)
    const currentViewDecision = determineGraphView(this.props);
    const desiredView = currentViewDecision.useFlowGraph;
    const isNowDefinitive = currentViewDecision.isDefinitive;

    let needsStateUpdate = false;
    let newState = {};

    // Update if:
    // 1. The view wasn't definitive before, but now is.
    // 2. The view *was* definitive, but the desired view changed (e.g., preference updated externally).
    // 3. The desired view differs from the current state *and* the new decision is definitive
    //    (prevents flapping if default is used temporarily).

    if (!prevState.isViewDefinitivelySet && isNowDefinitive) {
      // We just made a definitive choice (e.g., layouts arrived, or preference loaded)
      if (prevState.useFlowGraph !== desiredView) {
        newState.useFlowGraph = desiredView;
      }
      newState.isViewDefinitivelySet = true;
      needsStateUpdate = true; // Always update isViewDefinitivelySet flag
    } else if (prevState.isViewDefinitivelySet && isNowDefinitive) {
      // View was already definitive, check if the source *demands* a change (e.g., external preference update)
      // We only need to update if the *desired* view changed from the *current state*
      if (prevState.useFlowGraph !== desiredView) {
        // This typically happens if forceInitialGraphView or viewPreference changed
        newState.useFlowGraph = desiredView;
        needsStateUpdate = true;
      }
    }
    // No need for an 'else' here - if it's still not definitive, we don't update state based on the default.
    if (needsStateUpdate) {
      this.setState(newState);
    }
  }

  componentWillUnmount() {
    // Clear any pending timeouts
    if (this.searchGraphTimeout) {
      clearTimeout(this.searchGraphTimeout);
    }
  }

  // --- Graph Interaction Methods ---

  @boundMethod
  focusNodeByUri(nodeUri) {
    this.setState({ nodeUriToFocus: nodeUri });
  }

  @boundMethod
  focusEntityNode(toFocusEntity) {
    const { entity, node } = toFocusEntity;
    this.closeSearchPanel();
    const uniqueId = `node_${node.uri}_entity_${entity.uri}`;

    // Dispatch action to open the sidebar focused on the specific entity
    this.props.dispatch(
      setEntitiesSidebar({
        uniqueId,
        isOpen: true,
        name: node.name,
        uri: node.uri,
        ontologyType: node.ontologyType,
        forcedOpenUri: entity.uri,
        inputField: entity.name,
        preventFetch: true,
        uniqueValues: [entity],
        uniqueValuesTotalCount: 1
      })
    );
  }

  @boundMethod
  handleNodeFocused() {
    // Callback from graph components after focus animation/action is complete
    this.setState({ nodeUriToFocus: null });
  }

  // --- View Toggle Method ---

  @boundMethod
  toggleGraphView() {
    const newUseFlowGraph = !this.state.useFlowGraph;

    // Update state: View is now definitively set by user action
    this.setState({ useFlowGraph: newUseFlowGraph, isViewDefinitivelySet: true });

    // Update local storage preference
    this.props.setViewPreference({ useFlowGraph: newUseFlowGraph });

    // Track event
    trackEvent('KG Graph View Toggled', {
      changedToView: newUseFlowGraph ? GRAPH_VIEWS.FLOW : GRAPH_VIEWS.VIS
    });
  }

  // --- Search Methods ---

  @boundMethod
  searchNodes(searchQueryText) {
    const queryTerm = searchQueryText;
    this.setState({
      graphSearchScrollCount: 0,
      activeSelectableElement: 0
    });
    trackEvent('KG Visual Mode Searched', { query: searchQueryText });
    this.props.dispatch(searchGraph(queryTerm, 0));
  }

  @boundMethod
  handleSearchInputChange(searchQueryText) {
    if (this.searchGraphTimeout) {
      clearTimeout(this.searchGraphTimeout);
    }
    this.searchGraphTimeout = setTimeout(() => this.searchNodes(searchQueryText), SEARCH_DEBOUNCE_MS);
  }

  @boundMethod
  getMoreData() {
    this.props.dispatch(searchGraph(this.props.graphSearchResults?.query, this.state.graphSearchScrollCount + 1));
    this.setState({
      graphSearchScrollCount: this.state.graphSearchScrollCount + 1,
      shouldFocusOnInput: true
    });
  }

  @boundMethod
  openSearchPanel() {
    // Reset selection to the input field when opening
    this.setState({
      isFocusedOnSearch: true,
      activeSelectableElement: 0
    });
  }

  @boundMethod
  closeSearchPanel() {
    this.setState({
      isFocusedOnSearch: false,
      shouldFocusOnInput: false,
      activeSelectableElement: 0
    });
    // Clear pending search if panel is closed
    if (this.searchGraphTimeout) {
      clearTimeout(this.searchGraphTimeout);
    }
  }

  // --- Keyboard Navigation for Search ---

  @boundMethod
  handleKeyDown(e) {
    // this method could be cleaned up even more, was mainly taken over from existing code...
    const { key } = e;
    const { graphSearchResults } = this.props;
    const { activeSelectableElement } = this.state;

    if (key === 'ArrowUp' || key === 'ArrowDown' || key === 'Enter') {
      if (graphSearchResults?.query && Object.keys(graphSearchResults).length) {
        e.preventDefault();
        let unifiedGraphSearchResults = {
          classes: graphSearchResults.classes || [],
          literals: graphSearchResults.literals || [],
          entities: graphSearchResults.entities || {}
        };
        let commonLength =
          unifiedGraphSearchResults.classes.length +
          unifiedGraphSearchResults.literals.length +
          (unifiedGraphSearchResults?.entities?.results?.length || 0);

        if (key === 'ArrowUp') {
          this.setState(prevState => {
            let count = prevState.activeSelectableElement - 1;
            if (count !== 0 && count !== -1) {
              return { activeSelectableElement: count };
            }
            return null;
          });
          if (activeSelectableElement === SHOW_MORE_ACTIVE_INDEX) {
            const showMoreButton = document.getElementById('showMoreButton');
            if (showMoreButton) {
              showMoreButton.style.opacity = SHOW_MORE_NORMAL_OPACITY;
            }
            this.setState({ activeSelectableElement: commonLength });
          }
        } else if (key === 'ArrowDown') {
          if (activeSelectableElement === SHOW_MORE_ACTIVE_INDEX) return;
          this.setState(prevState => {
            let count = prevState.activeSelectableElement + 1;
            if (prevState.activeSelectableElement !== commonLength) {
              return { activeSelectableElement: count };
            }
            return null;
          });
          if (activeSelectableElement === commonLength) {
            const showMoreButton = document.getElementById('showMoreButton');
            if (showMoreButton) {
              showMoreButton.style.opacity = SHOW_MORE_DIMMED_OPACITY;
            }
            this.setState({ activeSelectableElement: SHOW_MORE_ACTIVE_INDEX });
          }
        } else if (key === 'Enter') {
          if (activeSelectableElement) {
            if (activeSelectableElement === SHOW_MORE_ACTIVE_INDEX) {
              const showMoreButton = document.getElementById('showMoreButton');
              if (showMoreButton) {
                showMoreButton.style.opacity = SHOW_MORE_NORMAL_OPACITY;
              }
              this.getMoreData();
              trackEvent('KG Visual Mode Searched');
              this.setState({ activeSelectableElement: commonLength + 1 });
            }
            document.activeElement.blur();
          }
        }
      }
    }
  }

  render() {
    const {
      isEmbedded,
      isHidden,
      graphSearchResults,
      history,
      graph,
      dispatch,
      hideSearchArea,
      hideGraphToggle,
      hideCloseButton,
      backgroundColor
    } = this.props;
    const { useFlowGraph, isFocusedOnSearch, shouldFocusOnInput, activeSelectableElement } = this.state;

    // Determine if search area should be shown
    const shouldShowSearchArea = !isHidden && !hideSearchArea;

    // Create search area component
    const searchArea = shouldShowSearchArea && (
      <SearchArea
        label="kgGraphSearch"
        shouldFocusOnInput={shouldFocusOnInput}
        query={graphSearchResults?.query || ''}
        onFocus={this.openSearchPanel}
        onKeyDown={this.handleKeyDown}
        onBlur={this.closeSearchPanel}
        onChange={this.handleSearchInputChange}
      />
    );

    // Show search results panel if search is focused
    const searchResultsPanel = isFocusedOnSearch && !isEmbedded && (
      <SearchResultsPanel
        focusClassNode={node => {
          this.focusNodeByUri(node.uri);
          this.closeSearchPanel();
        }}
        focusEntityNode={this.focusEntityNode}
        focusLiteralNode={node => {
          this.focusNodeByUri(node.uri);
          this.closeSearchPanel();
        }}
        focusMeasureNode={node => {
          this.focusNodeByUri(node.uri);
          this.closeSearchPanel();
        }}
        activeSelectableElement={activeSelectableElement}
        searchQueryText={graphSearchResults?.query || ''}
        closeSearchPanel={this.closeSearchPanel}
        getMoreData={this.getMoreData}
        graphSearchResults={graphSearchResults}
      />
    );

    // Apply custom background color if provided
    const graphStyle = backgroundColor ? { '--override-graph-background-color': backgroundColor } : {};

    return (
      <div className={styles.container}>
        {searchResultsPanel}
        <div className={styles.graphComponent}>
          <div className={styles.graphHeader}>
            <div className={styles.headerControls}>
              <div className={styles.searchContainer}>{searchArea}</div>

              <div className={styles.graphToggle}>
                <KnowledgeGraphHeaderOptions
                  useFlowGraph={useFlowGraph}
                  hideGraphToggle={hideGraphToggle}
                  toggleGraph={this.toggleGraphView}
                  hideCloseButton={hideCloseButton}
                  graph={graph}
                  onClose={() => {
                    changeUrlGivenCurrentQueryParams(history, {
                      visualMode: null
                    });
                  }}
                />
              </div>
            </div>
          </div>

          <div className={styles.graphWrapper}>
            <ErrorBoundary>
              <KnowledgeGraphLayoutManager
                graph={graph}
                dispatch={dispatch}
                fetchGraphLayout={fetchGraphLayout}
                style={graphStyle}
              >
                {useFlowGraph ? (
                  <KnowledgeGraphFlow
                    {...this.props}
                    nodeUriToFocus={this.state.nodeUriToFocus}
                    onNodeFocused={this.handleNodeFocused}
                    isHidden={isHidden}
                    style={graphStyle}
                  />
                ) : (
                  <KnowledgeGraphVis
                    {...this.props}
                    nodeUriToFocus={this.state.nodeUriToFocus}
                    onNodeFocused={this.handleNodeFocused}
                    isHidden={isHidden}
                    style={graphStyle}
                  />
                )}
              </KnowledgeGraphLayoutManager>
            </ErrorBoundary>
          </div>
        </div>
      </div>
    );
  }
}

const mapStateToProps = state => ({
  knowledgeGraphId: state.knowledgeGraphMeta.meta.id,
  hasWritePermission: state.knowledgeGraphMeta.meta.hasWritePermission,
  graphSearchResults: state.graphSearchResults,
  cssVars: state.theme.themeDetails.cssVars,
  suggestionsForBadNames: state.recipes.suggestionsForBadNames,
  flowNodeStructure: state.graph.flowNodeStructure
});

export default withTranslation('veezoo')(
  connect(mapStateToProps)(withLocalStoragePreference(withEmbedded(withRouter(KnowledgeGraphContainer))))
);
